import { StyleSheet } from 'aphrodite';
import { HEADER_CELL_TEXT } from '../../../../../components/DataGrid/styles';
import { THEME } from '../../../../../components-v2/Theme/theme';
import { CssSelectors } from '../../../../../styles/styles';

// the Files tab renders in a panel much narrower than the viewport, so the stacked
// layout keys off the wrapper's own width (container query), not a media query
const STACKED_QUERY = `@container (max-width: ${THEME.breakpoints.md}px)`;

export const styleSheet = StyleSheet.create({
	container: {
		height: '100%',
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		padding: THEME.spacing.lg,
		boxSizing: 'border-box',
	},
	header: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-end',
		width: '100%',
	},
	pageTitle: {
		display: 'flex',
		gap: THEME.spacing.md,
		alignItems: 'center',
		fontSize: THEME.typography.fontSizes.lg,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	iconContainer: {
		height: 48,
		width: 48,
		borderRadius: THEME.common.borderRadius,
		backgroundColor: '#DBEAFE',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
	},
	actionsContainer: {
		display: 'flex',
		flexDirection: 'row',
		gap: THEME.spacing.md,
	},
	content: {
		marginTop: THEME.spacing.lg,
	},
	dataGridWrapper: {
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		overflowX: 'auto',
		containerType: 'inline-size',
	},
	dataGrid: {
		borderBottom: 'none',
	},
	dataGridHeader: {
		[STACKED_QUERY]: {
			display: 'none',
		},
	},
	dataGridRow: {
		[STACKED_QUERY]: {
			display: 'grid',
			gridTemplateColumns: 'auto 1fr',
			alignItems: 'start',
			columnGap: THEME.spacing.md,
			padding: THEME.spacing.md,
			borderTop: THEME.common.border,
			':first-child': {
				borderTop: 'none',
			},
			// the base DataGrid row pads first/last cells via a higher-specificity child
			// selector; re-declare it here so the card's own padding is the only gutter
			...CssSelectors.allChildren({ paddingLeft: 0 }, ':first-child'),
			...CssSelectors.allChildren({ paddingRight: 0 }, ':last-child'),
		},
	},
	dataGridCell: {
		[STACKED_QUERY]: {
			border: 'none',
			padding: 0,
		},
	},
	cellCheckbox: {
		[STACKED_QUERY]: {
			width: 'auto',
		},
	},
	cellFilename: {
		[STACKED_QUERY]: {
			minWidth: 0,
			overflowWrap: 'anywhere',
		},
	},
	cellHiddenWhenStacked: {
		[STACKED_QUERY]: {
			display: 'none',
		},
	},
	selectAllRow: {
		display: 'none',
		[STACKED_QUERY]: {
			display: 'flex',
			// same higher-specificity first/last-cell padding re-declaration as dataGridRow,
			// so the select-all checkbox lines up with the row checkboxes below it
			...CssSelectors.allChildren({ paddingLeft: THEME.spacing.md }, ':first-child'),
			...CssSelectors.allChildren({ paddingRight: THEME.spacing.md }, ':last-child'),
		},
	},
	selectAllCell: {
		flex: 1,
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.md,
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
		backgroundColor: THEME.colors.hover.light,
	},
	selectAllLabel: {
		...HEADER_CELL_TEXT,
		color: THEME.colors.text.main,
	},
	filenameCell: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		[STACKED_QUERY]: {
			alignItems: 'flex-start',
			gap: THEME.spacing.md,
		},
	},
	fileDetails: {
		minWidth: 0,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: THEME.spacing.xs,
	},
	filenameLink: {
		height: 'auto',
		minWidth: 0,
		padding: 0,
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.bold,
		justifyContent: 'flex-start',
		textAlign: 'left',
	},
	fileMeta: {
		display: 'none',
		[STACKED_QUERY]: {
			display: 'flex',
			flexDirection: 'column',
			gap: THEME.spacing.xs,
		},
	},
	fileMetaLine: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.light,
	},
	sourceBadge: {
		display: 'inline-flex',
		alignItems: 'center',
		padding: `2px ${THEME.spacing.sm}px`,
		border: THEME.common.border,
		borderRadius: 6,
		backgroundColor: THEME.colors.background.light,
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	fileIcon: {
		flexShrink: 0,
	},
	actionsCell: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.xs,
	},
	actionButton: {
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		padding: THEME.spacing.xs,
		borderRadius: THEME.common.borderRadius,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		':hover': {
			backgroundColor: THEME.colors.hover.light,
		},
	},
	checkboxColumn: {
		width: 40,
		paddingRight: 0,
	},
	hiddenInput: {
		display: 'none',
	},
	fileList: {
		margin: 0,
		paddingLeft: THEME.spacing.md,
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
	},
	dropzone: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		border: `2px dashed ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		padding: THEME.spacing.xl,
		cursor: 'pointer',
		transition: 'all 0.2s',
		marginTop: THEME.spacing.md,
		':hover': {
			borderColor: THEME.colors.primary.main,
			backgroundColor: THEME.colors.primary.light,
		},
	},
	dropzoneActive: {
		borderColor: THEME.colors.primary.main,
		backgroundColor: THEME.colors.primary.light,
	},
	dropzoneContent: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		textAlign: 'center',
		pointerEvents: 'none',
	},
	dropzoneTitle: {
		margin: 0,
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.main,
	},
	dropzoneDescription: {
		margin: 0,
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.light,
	},
	uploadedFilesList: {
		marginTop: THEME.spacing.md,
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
	},
	filesCount: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	fileRows: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
		maxHeight: 200,
		overflowY: 'auto',
	},
	fileRow: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
		backgroundColor: THEME.colors.background.main,
		borderRadius: THEME.common.borderRadius,
		border: THEME.common.border,
	},
	fileRowName: {
		flex: 1,
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.main,
		...THEME.common.truncate,
	},
});
